import React from 'react';

import { Link } from 'react-router-dom';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { RecurrencyRole, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { ISODateStr } from '@recurrency/core-api-schema/dist/common/types';
import {
  ValidationRuleCategories,
  ValidationRuleStatus,
} from '@recurrency/core-api-schema/dist/validationRules/common';
import { ValidationRuleDTO } from '@recurrency/core-api-schema/dist/validationRules/getValidationRules';
import { ColumnType } from 'antd/lib/table';
import { colors } from 'theme/colors';

import { FORECAST_COLOR } from 'pages/purchasing/utils';

import { Card, CardHeader, CardSection } from 'components/Card';
import { Sparkline } from 'components/Sparkline';
import { Table } from 'components/Table';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { formatDate, formatNumber } from 'utils/formatting';
import { arePermissionsEnabled, hasUserPermission, isAtLeastOneOfRecurrencyRoles } from 'utils/roleAndTenant';
import { routes } from 'utils/routes';
import { asKeyOf } from 'utils/tables';
import { getTenantSetting } from 'utils/tenantSettings';

import { getItemsSelectedLastRun, getLocalTimezoneAbbreviation, getRunHistoryPoints } from './utils';

/**
 * The rules that decide which item-locations the nightly automation considers.
 *
 * Rule names link through to the rule builder only for users who can open it; everyone else sees the
 * names as plain text with a note that selection is powered by Rules.
 */
export function ItemSelectionCard() {
  const { activeTenant } = useGlobalApp();
  // only deployed rules select items; drafts are excluded server-side, as are inactive rules
  const { data, isLoading } = useCoreApi(schemas.validationRules.getValidationRules, {
    queryParams: { filter: { statuses: [ValidationRuleStatus.Deployed] } },
  });

  const canOpenRuleBuilder = arePermissionsEnabled()
    ? hasUserPermission(activeTenant, TenantUserPermission.AccessRuleBuilderRulesView)
    : isAtLeastOneOfRecurrencyRoles(activeTenant, [RecurrencyRole.TenantAdmin]);
  const nightlyLimit = getTenantSetting(TenantSettingKey.FeatureAutomatedMinMaxNightlyCap);
  const timezone = getLocalTimezoneAbbreviation();

  const rules = (data?.items ?? []).filter((rule) => rule.category === ValidationRuleCategories.MinMaxAutomation);

  const columns: ColumnType<ValidationRuleDTO>[] = [
    {
      title: 'Rule',
      dataIndex: asKeyOf<ValidationRuleDTO>('name'),
      render: (name: string, record) =>
        canOpenRuleBuilder ? <Link to={routes.ruleBuilder.ruleDetails(record.ruleId)}>{name}</Link> : name,
    },
    {
      title: `Last Run (${timezone})`,
      dataIndex: asKeyOf<ValidationRuleDTO>('lastRunTime'),
      width: 200,
      render: (lastRunTime: ISODateStr) => (lastRunTime ? formatDate(lastRunTime, true, true) : 'Not run yet'),
    },
    {
      title: 'Item-Locations Selected Last Run',
      dataIndex: asKeyOf<ValidationRuleDTO>('runs'),
      align: 'right',
      width: 240,
      // what the rule matched, before the nightly limit and the no-change filter narrow it down
      render: (_, record) => {
        const selected = getItemsSelectedLastRun(record.runs);
        return selected === undefined ? '-' : formatNumber(selected);
      },
    },
    {
      title: 'Run History',
      dataIndex: asKeyOf<ValidationRuleDTO>('runs'),
      width: 180,
      render: (_, record) =>
        record.runs.length > 0 ? (
          <Sparkline
            height={40}
            series={[
              {
                data: getRunHistoryPoints(record.runs),
                formatFn: (value: number) => formatNumber(value),
                title: 'Item-Locations Selected',
                color: FORECAST_COLOR,
                showGradient: true,
              },
            ]}
            useDateTime
          />
        ) : (
          '-'
        ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Item selection"
        description={
          nightlyLimit
            ? `Rules select which item-locations are eligible. Each night the ${formatNumber(
                nightlyLimit,
              )} with the largest changes are exported, largest change first.`
            : 'Rules select which item-locations are eligible. Each night those with the largest changes are exported, largest change first.'
        }
      />
      <CardSection>
        <Table
          data={rules}
          columns={columns}
          isLoading={isLoading}
          rowKey={asKeyOf<ValidationRuleDTO>('ruleId')}
          locale={{ emptyText: 'No deployed item selection rules.' }}
        />
        {!canOpenRuleBuilder && rules.length > 0 && (
          <div
            className={css`
              margin-top: 12px;
              color: ${colors.neutral[500]};
            `}
          >
            Contact your Recurrency administrator to change what is automatically exported.
          </div>
        )}
      </CardSection>
    </Card>
  );
}
